<template>
  <div id="tabla-totales-a-facturar-acuerdo-comercial">
    <div class="seccion-resumen" v-if="acuerdo_comercial">

      <!-- Titulo seccion resumen -->
      <div class="titulo-seccion-total-a-facturar">Total a facturar por el primer período</div>

      <!-- Resumen (Tabla total a facturar + descuento aplicado + total de la primera factura) -->
      <div class="contenido-resumen">

        <!-- Tabla totales a facturar -->
        <table class="tabla-resumen">
          <tr class="row-header-resumen">
            <td>DESCRIPCIÓN</td>
            <td class="columna-numerica">CANT.</td>
            <td class="columna-numerica">PRECIO UNIT.</td>
            <td class="columna-numerica tamanio-fijo">MONTO</td>
          </tr>
          <tr class="row-ufs">
            <td>Unidades funcionales</td>
            <td class="columna-numerica">
              <div>
                {{ obtenerUFsAFacturar }} <sup v-if="seFacturaPorDebajoDelMinimo" class="super-indice">(1)</sup>
              </div>
            </td>
            <td class="columna-numerica">$ {{ precioUF | formatMontoCentavos }}</td>
            <td class="columna-numerica tamanio-fijo">$ {{ precio_lista | formatMontoCentavos }}</td>
          </tr>
        </table>

        <!-- Descuento aplicado -->
        <div v-if="hayAlgunDescuentoAplicable" class="row-importe row-descuento-aplicado">
          <div>Descuentos</div>
          <div class="importe">- $ {{ bonificacion | formatMontoCentavos }}</div>
        </div>

        <!-- Total de la primera factura -->
        <div class="row-importe row-total">
          <div>
            {{ !hayAlgunDescuentoAplicable ? 'Total' : 'Total de la primera factura' }}
            <sup class="super-indice">{{ seFacturaPorDebajoDelMinimo ? '(2)' : '(1)' }}</sup>
          </div>
          <div class="importe">$ {{ precio_bonificado | formatMontoCentavos }}</div>
        </div>

      </div>

      <!-- Precio final UF -->
      <div class="alert-precio-final">

        <div v-if="hayAlgunDescuentoAplicable">
          <div class="label-precio-final mr-1">Precio final por UF facturable incluyendo descuentos: $ {{ precio_uf_final |  formatMontoCentavos }}</div>
        </div>

        <div class="label-iva">Todos los precios incluyen IVA.</div>

        <div class="label-aclaracion">

          <div v-if="seFacturaPorDebajoDelMinimo && hayAlgunDescuentoAplicable">
            <div>(1) El mínimo de UFs facturables es {{ minimo_ufs_plataforma }}.</div>
            <div>(2) El total de la factura en meses posteriores podría variar según los descuentos aplicados y actualizaciones en el precio base del servicio.</div>
          </div>

          <div v-if="!seFacturaPorDebajoDelMinimo && hayAlgunDescuentoAplicable">
            <div>(1) El total de la factura en meses posteriores podría variar según los descuentos aplicados y actualizaciones en el precio base del servicio.</div>
          </div>

          <div v-if="seFacturaPorDebajoDelMinimo && !hayAlgunDescuentoAplicable">
            <div>(1) El mínimo de UFs facturables es {{ minimo_ufs_plataforma }}.</div>
            <div>(2) El total de la factura en meses posteriores podría variar según actualizaciones en el precio base del servicio.</div>
          </div>

          <div v-if="!seFacturaPorDebajoDelMinimo && !hayAlgunDescuentoAplicable">
            <div>(1) El total de la factura en meses posteriores podría variar según actualizaciones en el precio base del servicio.</div>
          </div>

        </div>
      </div>

    </div>
  </div>
</template>

<script>
import { AcuerdoComercial } from '@/models/AcuerdoComercial';
import { TipoConsorcio } from '../acuerdo-comercial.consts';
import { helperDestructurarDescuentosUfs } from '../acuerdo-comercial.helpers';
import { Consorcio } from '@/models/Consorcio';
import { METADATA_GETTER_IVA, METADATA_GETTER_MINIMO_UFS_PLATAFORMA, METADATA_GETTER_PRECIO_UFS } from '@/store/metadata/metadata.types';

/**
 * Tabla para mostrar el total a facturar por el primer período de un acuerdo comercial
 * Este componente recibe por separado el acuerdo comercial y el consorcio para separar la lógica de cálculo de precios. En caso de que se cambie el consorcio, se recalculan los precios.
 */
export default {
  name: 'TablaTotalAFacturarAcuerdoComercial',
  props: {
    acuerdo_comercial: { type: AcuerdoComercial, default: null  },
    consorcio: { type: Consorcio, default: null }
  },
  data: () =>({
    precio_lista: 0, // Precio de lista (incluye IVA)
    precio_bonificado_sin_iva: 0,
    precio_bonificado: 0,
    bonificacion: 0, // Bonificación (incluye IVA)
    precio_uf_final: 0,

    // Lógica para los descuentos
    descuentos_temporales: [],
    descuento_permanente_uf: null,
    descuento_permanente_cocheras: null,

    minimo_ufs_plataforma: 0
  }),
  mounted() {

    this.minimo_ufs_plataforma = this.$store.getters[METADATA_GETTER_MINIMO_UFS_PLATAFORMA];

    // Seteo de descuentos temporales y permanentes (si el acuerdo comercial tiene descuentos en las ufs)
    if(this.acuerdo_comercial && this.acuerdo_comercial.descuentos_ufs) {
      const descuentos_ufs = helperDestructurarDescuentosUfs(this.acuerdo_comercial);
      this.descuentos_temporales = descuentos_ufs[0];
      this.descuento_permanente_uf = descuentos_ufs[1];
      this.descuento_permanente_cocheras = descuentos_ufs[2];
    }

    this.calcularPrecios();
  },
  computed: {

    /**
     * Computed para definir cuantas UFs se facturarán.
     *  - Si las UFs facturables del consorcio son menores al minimo establecido por la plataforma
     *    - Las UFs a facturar serán el minimo de la plataforma en caso que NO se permita facturar por debajo de minimo
     *    - Si se permite facturar por debajo del minimo de la plataforma, serán las del consorcio propiamente.
     *  - Si no es menor al minimo establecido por la plataforma
     *    - Las UFs a facturar, serán las UFs facturables del consorcio como tal
     */
    obtenerUFsAFacturar(){
      const cantidad_ufs_facturables_consorcio = this.consorcio.cantidad_ufs_facturables;
      if ((cantidad_ufs_facturables_consorcio < this.minimo_ufs_plataforma) && !this.acuerdo_comercial.minimo_ufs) {
        return this.minimo_ufs_plataforma;
      }

      return cantidad_ufs_facturables_consorcio;
    },

    /**
     * Computed para determinar si se facturará por debajo del minimo de UFs permitido
     * Este se calcula en base a las cantidad de ufs facturables sobre el minimo definido
     */
    seFacturaPorDebajoDelMinimo(){
      return this.acuerdo_comercial && this.consorcio.cantidad_ufs_facturables < this.obtenerUFsAFacturar;
    },

    /**
     * Computed para obtener el precio a mostrar y cobrar por UF dependiendo del tipo de consorcio (Se le aplica IVA)
     */
    precioUF(){
      const precio_ufs = this.$store.getters[METADATA_GETTER_PRECIO_UFS];
      if (this.consorcio && precio_ufs){
        let precio_uf = (this.consorcio.tipo === TipoConsorcio.EDIFICIO ? precio_ufs.precio_edificio : precio_ufs.precio_barrio) * this.$store.getters[METADATA_GETTER_IVA];
        return parseFloat(precio_uf.toFixed(2));
      }
      return 0;
    },

    /**
     * Computed para detectar si existe algun descuento, que se pueda aplicar, sobre cualquier tipo de UF
     */
    hayAlgunDescuentoAplicable(){
      return this.descuentos_temporales.length || this.descuento_permanente_uf || this.hayDescuentosCocherasAplicables;
    },

    /**
     * Computed para detectar si existen descuentos sobre cocheras y que sean aplicables
     * Que sean aplicables significa que existan cocheras a las cuales aplicarselo
     */
    hayDescuentosCocherasAplicables(){
      return this.descuento_permanente_cocheras && this.consorcio.cantidadCocheras > 0;
    }
  },
  watch: {

    /**
     * Necesario para detectar cuando cambio el consorcio seleccionado y recalcular los precios
     */
    consorcio() {
      this.calcularPrecios();
    }
  },
  methods: {

    /**
     * Metodo para calcular las variables utilizadas en la lógica para mostrar los precios
     * 1. precio lista: es el precio total de las UFs facturables, sin contemplar ningún descuento
     * 2. bonificacion: es la resta entre el precio de lista, sin descuentos, menos el precio total bonificado que sería aplicando los descuentos
     * 3. precio bonificado: es el precio total de las UFs facturables pero aplicandoles los descuentos
     * 4. precio bonificado sin iva: es el precio total de las Ufs facturables aplicando los descuentos pero sin incluir el IVA
     * 5. precio uf final: Es el precio final al cual queda cada UF facturada (sin incluir IVA)
     */
    calcularPrecios(){
      let precio_lista_con_iva = 0;
      let precio_bonificado_sin_iva = 0;
      let precio_bonificado_con_iva = 0;

      if (this.$store.getters[METADATA_GETTER_PRECIO_UFS] && this.consorcio){
        let precio_a_usar_uf_sin_iva = (this.consorcio.tipo === TipoConsorcio.EDIFICIO ? this.$store.getters[METADATA_GETTER_PRECIO_UFS].precio_edificio : this.$store.getters[METADATA_GETTER_PRECIO_UFS].precio_barrio);
        let precio_a_usar_uf_con_iva = parseFloat((precio_a_usar_uf_sin_iva * this.$store.getters[METADATA_GETTER_IVA]).toFixed(2));

        // Si cargo menos del minimo de UFs permitidos, se calcula el precio con el minimo de UFs permitidas.
        precio_lista_con_iva = this.obtenerUFsAFacturar * precio_a_usar_uf_con_iva;

        precio_bonificado_sin_iva = parseFloat(this.calcularMontoBonificado().toFixed(2));
        precio_bonificado_con_iva = parseFloat((precio_bonificado_sin_iva * this.$store.getters[METADATA_GETTER_IVA]).toFixed(2));
      }

      this.precio_lista = precio_lista_con_iva;
      this.precio_bonificado_sin_iva = precio_bonificado_sin_iva;
      this.precio_bonificado = precio_bonificado_con_iva;
      this.bonificacion = parseFloat((precio_lista_con_iva - precio_bonificado_con_iva).toFixed(2));
      this.precio_uf_final = parseFloat((this.precio_bonificado / this.obtenerUFsAFacturar).toFixed(2));
    },

    /**
     * Metodo para calcular el total del precio luego de la aplicación de todos lo descuentos permantentes y temporales
     * IMPORTANTE: No incluye IVA
     */
    calcularMontoBonificado() {
      let precio_total_bonificado = 0;
      const precio_ufs = this.$store.getters[METADATA_GETTER_PRECIO_UFS];

      if (this.consorcio && precio_ufs){

        // Tipos UFs en EDIFICIOS: Depto / Cochera / Locales
        if (this.consorcio.tipo === TipoConsorcio.EDIFICIO){
          let cantidad_deptos = this.consorcio.cantidadDepartamentos;
          let cantidad_cocheras = this.consorcio.cantidadCocheras;
          let cantidad_locales = this.consorcio.cantidadLocales;

          // A todas las tipos de ufs distintas, se le factura lo mismo
          let precio_final_depto = precio_ufs.precio_edificio ?? 0;
          let precio_final_cochera = precio_ufs.precio_edificio ?? 0;
          let precio_final_local = precio_ufs.precio_edificio ?? 0;

          // PERMANENTES
          /**
           * Algoritmo: Primero se aplica el general (si es que tiene) y luego al resultado se aplica el de cocheras (si es que tiene)
           * En otras palabras: son acumulables.
           */
          if (this.descuento_permanente_uf){ // General
            precio_final_depto = precio_final_depto - ((precio_final_depto / 100) * this.descuento_permanente_uf);
            precio_final_cochera = precio_final_cochera - ((precio_final_cochera / 100) * this.descuento_permanente_uf);
            precio_final_local = precio_final_local - ((precio_final_local / 100) * this.descuento_permanente_uf);
          }

          if (this.descuento_permanente_cocheras){ // Acumulo si es que tiene descuento permanente para las cocheras
            precio_final_cochera = precio_final_cochera - ((precio_final_cochera / 100) * this.descuento_permanente_cocheras);
          }

          // TEMPORALES (luego de calcular los permanentes, se acumula el primer temporal)
          if (this.descuentos_temporales.length){
            precio_final_depto = precio_final_depto - ((precio_final_depto / 100) * this.descuentos_temporales[0].descuento);
            precio_final_cochera = precio_final_cochera - ((precio_final_cochera / 100) * this.descuentos_temporales[0].descuento);
            precio_final_local = precio_final_local - ((precio_final_local / 100) * this.descuentos_temporales[0].descuento);
          }

          // Calculo del precio total final dependiendo de la cantidad de UFs facturables y si estableció facturar por debajo del mínimo o no
          if (this.consorcio.cantidad_ufs_facturables <= this.minimo_ufs_plataforma) {
            if (this.acuerdo_comercial && this.acuerdo_comercial.minimo_ufs){
              precio_total_bonificado = (precio_final_depto * cantidad_deptos)  + (precio_final_cochera * cantidad_cocheras) + (precio_final_local * cantidad_locales);
            } else {
              let cantidad_rellenar = this.minimo_ufs_plataforma - this.consorcio.cantidad_ufs_facturables;
              precio_total_bonificado = (precio_final_depto * cantidad_deptos)  + (precio_final_cochera * cantidad_cocheras) + (precio_final_local * cantidad_locales) + (precio_final_depto * cantidad_rellenar);
            }
          } else {
            precio_total_bonificado = (precio_final_depto * cantidad_deptos)  + (precio_final_cochera * cantidad_cocheras) + (precio_final_local * cantidad_locales);
          }

        } else { // Tipos UFs en BARRIOS: Lote
          let cantidad_lotes = this.consorcio.cantidadLotes;
          let precio_final_lote = precio_ufs.precio_barrio ?? 0;

          // PERMANENTES
          if (this.descuento_permanente_uf){
            precio_final_lote = precio_final_lote - ((precio_final_lote / 100) * this.descuento_permanente_uf);
          }

          // TEMPORALES
          if (this.descuentos_temporales.length){
            precio_final_lote = precio_final_lote - ((precio_final_lote / 100) * this.descuentos_temporales[0].descuento);
          }

          // Calculo del precio total final dependiendo de la cantidad de UFs facturables y si estableció facturar por debajo del mínimo o no
          let ufs_a_facturar = cantidad_lotes;
          if (this.consorcio.cantidad_ufs_facturables <= this.minimo_ufs_plataforma){
            ufs_a_facturar = this.acuerdo_comercial && this.acuerdo_comercial.minimo_ufs ? this.consorcio.cantidad_ufs_facturables : this.minimo_ufs_plataforma;
          }
          precio_total_bonificado = (precio_final_lote * ufs_a_facturar);
        }
      }

      return precio_total_bonificado;
    }
  }
};

</script>

<style lang="scss">
#tabla-totales-a-facturar-acuerdo-comercial {

  .seccion-resumen {

    .titulo-seccion-total-a-facturar {
      font-size: 14px;
      line-height: 18px;
      color: color(dark-secund);
      font-weight: 700;
    }

    .contenido-resumen {
      margin-top: 8px;

      .super-indice {
        font-size: 10px;
        font-weight: 400;
      }

      .tabla-resumen {
        width: 100%;
        border-bottom: 1px solid color(info);
        border-collapse: collapse;

        .row-header-resumen {
          background-color: color(light);
          border: 1px solid color(gris-claro-secund);

          td {
            padding: 8px 16px;
            font-size: 12px;
            font-weight: 500;
            line-height: 14.4px;
            color: color(dark);
            white-space: nowrap;
          }

        }

        .row-ufs {
          td {
            padding: 16px;
            font-size: 14px;
            font-weight: 400;
            line-height: 16.8px;
            color: color(dark);
            white-space: nowrap;
          }
        }

        .columna-numerica {
          text-align: right;
        }

        .tamanio-fijo {
          width: 164px;
        }
      }

      .row-importe {
        display: flex;
        flex-direction: row;
        justify-content: flex-end;
        align-items: flex-end;
        padding-right: 16px;

        .importe {
          width: 164px;
          text-align: right;
        }
      }

      .row-descuento-aplicado {
        font-size: 18px;
        font-weight: 700;
        line-height: 21.6px;
        color: #007B40; // [ToDo] agregar a colores globales
        margin-top: 16px;
      }

      .row-total {
        font-size: 18px;
        font-weight: 700;
        line-height: 21.6px;
        color: color(dark);
        margin-top: 12px;
      }
    }
  }

  .alert-precio-final {
    display:flex;
    flex-direction: column;
    margin-top: 32px;

    padding: 10px 16px;
    border-radius: 8px;
    background-color: color(light);

    .label-precio-final {
      font-size: 14px;
      font-weight: 500;
      line-height: 16.8px;
      color: #007B40; // [ToDo] agregar a colores globales
      margin-bottom: 6px;
    }

    .label-iva {
      font-size: 13px;
      font-weight: 400;
      line-height: 15.6px;
      color: color(dark);
    }

    .label-aclaracion {
      font-size: 12px;
      font-weight: 400;
      line-height: 18px;
      color: color(dark);
      margin-top: 14px;
    }
  }

}
</style>